import React from 'react';
import AudioPlayer from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';

import { COMPARE_MIN_ITEMS, isYoutubeUrl, normalizeContextContent } from './contextLayouts';
import { nextLabel } from '../gameFlow';

/**
 * LA PAGE DE CONTEXTE — ce qu'on projette entre deux séries de décisions.
 *
 * UNE FORME PAR FORMAT, plutôt qu'une mise en page unique qu'on ampute. Ce
 * composant ne dessinait qu'un seul écran : une rangée de deux moitiés, média à
 * gauche, « légende » à droite, dont on masquait des morceaux selon trois
 * drapeaux. La moitié droite restait donc réservée même vide, une diapo ne
 * pouvait pas remplir l'écran sans une case cochée dont le nom parlait d'autre
 * chose, et il n'existait aucun moyen d'écrire une page de texte seul — alors
 * que c'est ce type de page, à lui seul, qui porte les trois quarts de la base.
 *
 * Chaque format ci-dessous est une forme complète et sans réglage : ce que
 * l'auteur choisit, c'est un écran, pas une combinaison d'interrupteurs. Le
 * registre des formats et la lecture des documents d'avant sont dans
 * `contextLayouts.js` ; ici, il n'y a plus que du rendu.
 *
 * LE MODE ÉDITION. Le composeur monte cette même page — pas une imitation — et
 * lui passe `editMode`. Les emplacements deviennent alors cliquables et les
 * emplacements VIDES se montrent au lieu de disparaître : c'est la seule façon
 * de voir, en composant, ce qui manque encore.
 */

/** Un emplacement du format : cliquable en composition, muet s'il est vide en séance. */
function Slot({ editMode, onEdit, hint, filled, className = '', children }) {
  if (filled) {
    if (!editMode) return <div className={className}>{children}</div>;
    return (
      <div
        className={`${className} ctx-slot is-editable`}
        onClick={onEdit}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onEdit?.()}
        title={hint}
      >
        {children}
        <span className="ctx-slot-pencil" aria-hidden="true">✏️</span>
      </div>
    );
  }
  // Vide : rien en séance — une page à laquelle il manque un morceau ne doit
  // pas afficher le trou à la salle. En composition, au contraire, le trou EST
  // l'information.
  if (!editMode) return null;
  return (
    <div className={`${className} ctx-slot is-empty`} onClick={onEdit} role="button" tabIndex={0}>
      <span className="ctx-slot-empty-label">{hint}</span>
    </div>
  );
}

/** Une image, une vidéo hébergée, ou une vidéo YouTube — le cadre décide de la taille. */
function ContextMedia({ url, type, className = '' }) {
  if (type === 'video') {
    if (isYoutubeUrl(url)) {
      return (
        <iframe
          className={`ctx-media ctx-media--yt ${className}`}
          src={url}
          style={{ border: 0 }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="Vidéo"
        />
      );
    }
    return (
      <video className={`ctx-media ctx-media--video ${className}`} controls>
        <source src={url} type="video/mp4" />
        Votre navigateur ne peut pas lire cette vidéo.
      </video>
    );
  }
  return <img className={`ctx-media ctx-media--image ${className}`} src={url} alt="" />;
}

function ContextAudio({ url }) {
  return (
    <div className="ctx-audio">
      <AudioPlayer autoPlay src={url} />
    </div>
  );
}

/* ─── Les cinq formats ─────────────────────────────────────────────── */

/** DIAPO : un visuel projeté entier, aussi grand que l'écran le permet. */
function SlideLayout({ content, editMode, onEdit }) {
  return (
    <div className="ctx-stage ctx-stage--slide">
      <Slot
        editMode={editMode}
        onEdit={onEdit}
        filled={!!content.media_url}
        hint="Déposer le visuel de la diapo"
        className="ctx-slide-frame"
      >
        <ContextMedia url={content.media_url} type={content.media_type} />
      </Slot>
      {content.audio_url && <ContextAudio url={content.audio_url} />}
    </div>
  );
}

/** MÉDIA ET PANNEAU : le visuel d'un côté, ce qui l'explique de l'autre. */
function SplitLayout({ content, editMode, onEdit }) {
  return (
    <div className="ctx-stage ctx-stage--split">
      <Slot
        editMode={editMode}
        onEdit={onEdit}
        filled={!!content.media_url}
        hint="Déposer l'image ou la vidéo"
        className="ctx-split-media"
      >
        <ContextMedia url={content.media_url} type={content.media_type} />
      </Slot>

      <div className="ctx-split-panel">
        <Slot
          editMode={editMode}
          onEdit={onEdit}
          filled={!!content.text}
          hint="Écrire le texte du panneau"
          className="ctx-text-card"
        >
          <p className="ctx-text">{content.text}</p>
        </Slot>
        {content.audio_url && <ContextAudio url={content.audio_url} />}
      </div>
    </div>
  );
}

/** TEXTE SEUL : une adresse à la salle. Le fond de la page reste visible. */
function TextLayout({ content, editMode, onEdit }) {
  return (
    <div className="ctx-stage ctx-stage--text">
      <Slot
        editMode={editMode}
        onEdit={onEdit}
        filled={!!content.text}
        hint="Écrire le texte de la page"
        className="ctx-text-card ctx-text-card--lead"
      >
        <p className="ctx-text ctx-text--lead">{content.text}</p>
      </Slot>
      {content.audio_url && <ContextAudio url={content.audio_url} />}
    </div>
  );
}

/**
 * PLEIN CADRE : l'image occupe l'écran, le texte passe par-dessus.
 *
 * Le visuel est posé en fond (cf. `ContextPage` plus bas) et non ici : c'est ce
 * qui lui permet d'aller jusqu'aux bords, sous le voile et sous le bouton, avec
 * le même positionnement fixe que le décor des autres pages.
 */
function CoverLayout({ content, editMode, onEdit }) {
  return (
    <div className="ctx-stage ctx-stage--cover">
      <Slot
        editMode={editMode}
        onEdit={onEdit}
        filled={!!content.text}
        hint="Écrire le texte posé sur l'image"
        className="ctx-cover-caption"
      >
        <p className="ctx-text ctx-text--overlay">{content.text}</p>
      </Slot>
      {content.audio_url && <ContextAudio url={content.audio_url} />}
    </div>
  );
}

/**
 * Les deux termes minimaux d'une comparaison, TOUJOURS montrés en composition.
 * Un document écrit à la main peut annoncer le format sans porter d'items ; le
 * canevas afficherait alors une bande vide, sans rien à cliquer pour la remplir.
 */
function padded(items) {
  const list = [...items];
  while (list.length < COMPARE_MIN_ITEMS) list.push({ media_url: '', media_type: 'photo', caption: '' });
  return list;
}

/** COMPARAISON : deux ou trois visuels côte à côte, chacun légendé. */
function CompareLayout({ content, editMode, onEdit }) {
  // En composition on montre les emplacements vides ; en séance, seuls les
  // termes réellement remplis — comparer avec une case vide ne compare rien.
  const items = editMode ? padded(content.items) : content.items.filter((item) => item.media_url);

  return (
    <div className="ctx-stage ctx-stage--compare">
      {content.text && (
        <Slot
          editMode={editMode}
          onEdit={onEdit}
          filled
          hint="Modifier la phrase d'introduction"
          className="ctx-compare-lead"
        >
          <p className="ctx-text">{content.text}</p>
        </Slot>
      )}

      <div className="ctx-compare-row">
        {items.map((item, index) => (
          <div className="ctx-compare-item" key={index}>
            <Slot
              editMode={editMode}
              onEdit={onEdit}
              filled={!!item.media_url}
              hint={`Déposer le visuel ${index + 1}`}
              className="ctx-compare-media"
            >
              <ContextMedia url={item.media_url} type={item.media_type} />
            </Slot>
            {(item.caption || editMode) && (
              <p className="ctx-compare-caption">
                {item.caption || (editMode ? <span className="ctx-muted">Sans légende</span> : '')}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const LAYOUTS = {
  slide: SlideLayout,
  split: SplitLayout,
  text: TextLayout,
  cover: CoverLayout,
  compare: CompareLayout,
};

const ContextPage = ({ pageData, gameStatus, onDecisionSelect, displayButton = true, endsGame = false, editMode = false, onEditContent }) => {
  const content = normalizeContextContent(pageData.content);
  const Layout = LAYOUTS[content.layout] || SlideLayout;

  const handleNextClick = () => {
    onDecisionSelect({
      page_id: pageData._id,
      page_type: 'context',
      page_general_consequence: pageData.page_general_consequence,
    });
  };

  // Le plein cadre remplace le décor habituel par son propre visuel : net, à
  // pleine opacité, recadré. Sans média — ou avec une vidéo, que ce format
  // n'accepte pas — il retombe sur l'image de fond, sans quoi le texte
  // flotterait sur un aplat.
  const coverUrl =
    content.layout === 'cover' && content.media_type === 'photo' ? content.media_url : '';
  const backdropUrl = coverUrl || content.background_image_url;

  return (
    <div className="page-general-container context-page">
      <div className="page-general-background-color" />

      <div className="page-general-background-hero" style={{ backgroundImage: 'url(/img/bgrille-m.svg)' }}>
        <div
          className={`page-general-background-image${coverUrl ? ' is-cover' : ''}`}
          style={{ backgroundImage: `url(${backdropUrl})` }}
        />
      </div>

      <div className="page-main-content">
        <Layout content={content} editMode={editMode} onEdit={onEditContent} />
        {displayButton && <button className="blue-button" onClick={handleNextClick}>{nextLabel(endsGame)}</button>}
      </div>
    </div>
  );
};

export default ContextPage;
